
















import 'nouislider/dist/nouislider.min.css';

import {
  computed, defineComponent, inject, onMounted, PropType, ref, useRoute, watch,
} from '@nuxtjs/composition-api';
import { isArray } from 'lodash-es';
import NoUiSlider, { API, Options } from 'nouislider';

import { useTax } from '~/composables';
import type { Aggregation } from '~/modules/GraphQL/types';

interface Slider extends HTMLElement {
  noUiSlider?: API;
}
const rangeTypeInitialValues = {}

export default defineComponent({
  name: 'RangeType',
  components: {},
  props: {
    filter: {
      type: Object as PropType<Aggregation>,
      required: true,
    },
    modelValue: {
      type: Array,
      default: () => ([]),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const route = useRoute();
    const { rawPriceWithCurrency, taxState } = useTax();
    const { isFilterSelected } = inject('UseFiltersProvider');

    if (!Object.values(rangeTypeInitialValues).length || !rangeTypeInitialValues[props.filter.attribute_code]) {
      rangeTypeInitialValues[props.filter.attribute_code] = [+(props.filter.options?.[0].value?.split('_')?.[0] || 0), +(props.filter.options?.[props.filter.options.length - 1]?.value?.split('_')?.[1] || 0)]
    }
    //if we got bigger filters for any reason we need to update to bigger values
    if (+(props.filter.options?.[0].value?.split('_')?.[0] || 0) < rangeTypeInitialValues[props.filter.attribute_code][0]) {
      rangeTypeInitialValues[props.filter.attribute_code][0] = +(props.filter.options?.[0].value?.split('_')?.[0] || 0);
    }
    //if we got bigger filters for any reason we need to update to bigger values
    if (+(props.filter.options?.[props.filter.options.length - 1].value?.split('_')?.[1] || 0) > rangeTypeInitialValues[props.filter.attribute_code][1]) {
      rangeTypeInitialValues[props.filter.attribute_code][1] = +(props.filter.options?.[props.filter.options.length - 1].value?.split('_')?.[1] || 0);
    }

    const slider = ref<Slider>(null);
    const initialRange = ref<number[]>(rangeTypeInitialValues[props.filter.attribute_code]);
    const sliderRange = ref<number[]>(initialRange.value);
    const selected = computed(() => isFilterSelected(props.filter.attribute_code, null, true));

    const currentRange = computed(() => {
      const priceFromQuery = `${route.value.query?.[props.filter.attribute_code]}`?.split('_');

      return [
        +priceFromQuery[0] > sliderRange.value[0] ? +priceFromQuery[0] : sliderRange.value[0],
        +priceFromQuery[1] < sliderRange.value[1] ? +priceFromQuery[1] : sliderRange.value[1],
      ];
    });

    const totalValue = computed(() => props.filter.options.reduce((acc, obj) => acc + (+obj.count), 0));
    const sliderOptions = computed<Options>(() => {
      const isPrice = props.filter.attribute_code?.toLowerCase()?.includes('price');

      return {
        start: currentRange.value,
        connect: true,
        range: {
          min: initialRange.value[0],
          max: initialRange.value[1],
        },
        tooltips: [
          { to(value) { return isPrice ? rawPriceWithCurrency(value) : Number.parseInt(`${value}`, 10); } },
          { to(value) { return isPrice ? rawPriceWithCurrency(value) : Number.parseInt(`${value}`, 10); } },
        ],
      };
    });

    const getOption = (sliderValue) => {
      const values = isArray(sliderValue) ? sliderValue : [sliderValue];
      const formattedValues = values.map((val) => Number.parseInt(`${val}`, 10));
      const label = formattedValues.join('-');
      const value = formattedValues.join('_');

      return {
        count: totalValue.value,
        label,
        value,
      };
    };

    const resetSlider = (el: Slider) => {
      el.noUiSlider.updateOptions({
        start: sliderRange.value,
        range: {
          min: initialRange.value[0],
          max: initialRange.value[1],
        },
      }, false);
    };

    onMounted(() => {
      if (currentRange.value) {
        sliderRange.value = currentRange.value;
      }

      NoUiSlider.create(slider.value, sliderOptions.value);
      slider.value.noUiSlider.on('change', (values) => {
        const option = getOption(values);
        emit('selectFilter', option);
        sliderRange.value = values.map((value) => +value);
      });
    });

    watch([sliderRange, taxState, selected], () => {
      if (!selected.value) {
        sliderRange.value = initialRange.value;
      }

      if (slider.value) {
        resetSlider(slider.value);
      }
    });

    const noUiSlider = computed(() => slider.value.noUiSlider);

    return {
      slider,
      noUiSlider,
      selected,
      sliderRange,
    };
  },
});
